// Contest rules per CCCD category, scoped so each team only ever sees the
// rules for the category they are registered in — see
// openspec/changes/cccd-category-scoped-contest-info. Static copy tied to
// the CCCD 2026 edition; keys must match the active event's/regional's
// CccdEventCategory.slug values (confirmed in gestion — see tasks.md 1.1).

export interface AltimeterSpec {
  models: string
  dimensions: string
  weight: string
}

export interface RocketSpec {
  minLength: string
  maxMass: string
  altimeter: string
  recovery: string
}

export interface EvaluationLevel {
  level: "verde" | "amarillo" | "rojo"
  label: string
  description: string
}

export interface MotorParticipation {
  label: string
  motor: string
  targetAltitude?: string
  maxMass?: string
}

export interface CategoryDocument {
  title: string
  // Static path/URL baked into the frontend build (public asset or external
  // link) — documents are not admin-managed or database-backed by design.
  url: string
}

export interface CategoryContestContent {
  slug: string
  title: string
  challengeName: string
  intro: string
  challengeDetails: string[]
  providedByOrganization?: string[]
  motorParticipations?: MotorParticipation[]
  flightTimeWindow?: string
  altimeter: AltimeterSpec
  rocketSpec: RocketSpec
  engineeringRequirements?: string[]
  payloadRestrictions?: string[]
  recoveryEvaluation: EvaluationLevel[]
  stabilityEvaluation: EvaluationLevel[]
  scoringCriteria: string[]
  mission: string[]
  // Downloadable reglamentos/formatos/anexos for this category — hardcoded
  // paths, filled in per contest edition. Empty until provided.
  documents: CategoryDocument[]
}

const ALTIMETER_SPEC: AltimeterSpec = {
  models: "Jolly Logic o Estes",
  dimensions: "aproximadamente 15 × 20 × 55 mm",
  weight: "aproximadamente 10–12 g",
}

export const CONTEST_CATEGORIES: Record<string, CategoryContestContent> = {
  "categoria-1": {
    slug: "categoria-1",
    title: "Categoría 1 · Principiantes",
    challengeName: "El Huevonauta",
    intro:
      "Construye, junto con tu equipo, un cohete capaz de llevar un huevo de gallina crudo durante todo el vuelo y lograr que regrese a tierra completamente intacto.",
    challengeDetails: [
      "La misión principal es salvar el huevo: si se rompe, el vuelo queda fuera de clasificación, sin importar la altura alcanzada.",
      "El huevo debe sobrevivir al despegue, ascenso, descenso y aterrizaje.",
      "Solo si el huevo llega intacto el equipo puede competir por la clasificación de altura.",
      "El cohete debe superar una altura mínima de 100 metros para que el vuelo sea válido; a partir de ahí, entre más alto mejor el resultado — siempre que el huevo llegue sano y salvo.",
    ],
    providedByOrganization: [
      "Un huevo de gallina crudo, con una masa entre 55 y 60 gramos.",
      "Un motor comercial de combustible sólido D12-5.",
    ],
    altimeter: ALTIMETER_SPEC,
    rocketSpec: {
      minLength: "40 cm",
      maxMass: "300 g",
      altimeter: "Obligatorio",
      recovery: "Sistema de recuperación mediante paracaídas",
    },
    recoveryEvaluation: [
      { level: "verde", label: "Apertura óptima", description: "El paracaídas abre completamente y cumple correctamente su función." },
      { level: "amarillo", label: "Apertura parcial", description: "El paracaídas abre de manera incompleta o enredada, pero ayuda a disminuir la velocidad de descenso." },
      { level: "rojo", label: "Sin apertura", description: "El paracaídas no abre y no cumple su función." },
    ],
    stabilityEvaluation: [
      { level: "verde", label: "Muy estable", description: "El cohete asciende de manera recta y controlada." },
      { level: "amarillo", label: "Medianamente estable", description: "El cohete comienza a desviarse o torcerse durante el vuelo." },
      { level: "rojo", label: "Inestable", description: "El cohete se desvía considerablemente o pierde estabilidad durante el ascenso." },
    ],
    scoringCriteria: [
      "El huevo llega intacto a tierra (requisito de clasificación).",
      "Altura alcanzada por encima de los 100 metros mínimos.",
      "Desempeño del sistema de recuperación (paracaídas).",
      "Estabilidad del cohete durante el ascenso.",
    ],
    mission: [
      "Construye un cohete pequeño, liviano y estable.",
      "Protege el huevo.",
      "Supera los 100 metros de altura.",
      "Registra tu vuelo con un altímetro de cualquiera de estas marcas: Jolly Logic o Estes.",
      "Haz que el paracaídas funcione correctamente.",
      "Consigue que tu cohete regrese a tierra de forma segura.",
    ],
    documents: [],
  },

  "categoria-2": {
    slug: "categoria-2",
    title: "Categoría 2 · Intermedios",
    challengeName: "Carga Útil Electrónica Libre y Altura Objetivo",
    intro:
      "Diseña y construye, junto con tu equipo, un cohete capaz de transportar una carga útil electrónica desarrollada por ustedes mismos y alcanzar una altura objetivo con la mayor precisión posible.",
    challengeDetails: [
      "El cohete debe integrar y transportar una carga útil electrónica libre, con objetivos científicos, tecnológicos o de ingeniería, diseñada y construida por el propio equipo.",
      "La carga útil electrónica será evaluada en tierra por los jurados antes del vuelo, considerando el desarrollo realizado por el equipo y el cumplimiento de los criterios establecidos.",
      "El reto no es llegar lo más alto posible, sino acertar: acércate lo máximo posible a la altura objetivo de tu participación. Pasarte o quedarte corto te aleja del objetivo.",
    ],
    motorParticipations: [
      { label: "Primera participación en Categoría 2", motor: "E12-4", targetAltitude: "200 metros", maxMass: "480 g" },
      { label: "Segunda participación en Categoría 2", motor: "F15-6", targetAltitude: "300 metros", maxMass: "650 g" },
    ],
    flightTimeWindow: "Entre 30 y 40 segundos de tiempo total de vuelo.",
    altimeter: ALTIMETER_SPEC,
    rocketSpec: {
      minLength: "60 cm",
      maxMass: "480 g con motor E12-4 / 650 g con motor F15-6",
      altimeter: "Obligatorio",
      recovery: "Sistema de recuperación mediante paracaídas",
    },
    recoveryEvaluation: [
      { level: "verde", label: "Apertura óptima", description: "El paracaídas abre completamente y cumple correctamente su función." },
      { level: "amarillo", label: "Apertura parcial", description: "El paracaídas abre de manera incompleta o enredada, pero ayuda a disminuir la velocidad de descenso." },
      { level: "rojo", label: "Sin apertura", description: "El paracaídas no abre y no cumple su función." },
    ],
    stabilityEvaluation: [
      { level: "verde", label: "Muy estable", description: "El cohete asciende de manera recta y controlada." },
      { level: "amarillo", label: "Medianamente estable", description: "El cohete comienza a desviarse o torcerse durante el vuelo." },
      { level: "rojo", label: "Inestable", description: "El cohete se desvía considerablemente o pierde estabilidad durante el ascenso." },
    ],
    scoringCriteria: [
      "Precisión respecto a la altura objetivo (200 m o 300 m, según motor asignado).",
      "Tiempo total de vuelo, dentro de la ventana de 30 a 40 segundos.",
      "Desempeño del sistema de recuperación.",
      "Estabilidad del cohete durante el ascenso.",
      "Evaluación en tierra de la carga útil electrónica, previa al vuelo.",
    ],
    mission: [
      "Diseña y construye un cohete estable, preciso y confiable.",
      "Desarrolla tu propia carga útil electrónica y preséntala para evaluación en tierra antes del vuelo.",
      "Utiliza el motor correspondiente a la participación de tu equipo.",
      "Acércate lo máximo posible a los 200 m o 300 m de altura objetivo.",
      "Mantén un tiempo de vuelo entre 30 y 40 segundos.",
      "Registra la altura con un altímetro de cualquiera de estas marcas: Jolly Logic o Estes.",
      "Haz que tu sistema de recuperación funcione correctamente.",
    ],
    documents: [],
  },

  "categoria-3": {
    slug: "categoria-3",
    title: "Categoría 3 · Universitarios",
    challengeName: "Carga Útil Tipo CanSat",
    intro:
      "Integra diseño estructural, simulación, aerodinámica, estabilidad, sistemas de recuperación y una carga útil funcional tipo CanSat en un cohete de combustible sólido, alcanzando una altura objetivo con la mayor precisión posible.",
    challengeDetails: [
      "El cohete debe integrar y transportar una carga útil funcional tipo CanSat: un sistema electrónico compacto, similar en tamaño a una lata, con sensores, electrónica, procesamiento de datos u otros experimentos científicos o tecnológicos, diseñado y construido por el propio equipo.",
      "Masa de la carga útil: entre 250 y 300 gramos — debe tenerse en cuenta desde las primeras etapas de diseño y simulación del cohete.",
      "El reto es la precisión, no la altura máxima: acércate lo máximo posible a la altura objetivo determinada por el motor asignado a tu equipo. Pasarte o quedarte corto te aleja del objetivo.",
      "La instalación del motor dentro del cohete es responsabilidad del equipo.",
    ],
    motorParticipations: [
      { label: "Primera participación en Categoría 3", motor: "G80-7T", targetAltitude: "400 metros" },
      { label: "Equipos que ya han participado anteriormente en Categoría 3", motor: "H135W-14A", targetAltitude: "650 metros" },
    ],
    altimeter: ALTIMETER_SPEC,
    rocketSpec: {
      minLength: "80 cm",
      maxMass: "Calculada por el equipo según la relación empuje/peso mínima y el motor asignado",
      altimeter: "Obligatorio",
      recovery: "Sistema de recuperación mediante paracaídas",
    },
    engineeringRequirements: [
      "Relación empuje/peso mínima de 5:1 — la masa máxima admisible depende del motor asignado y debe calcularla cada equipo.",
      "Margen mínimo de estabilidad estática de 2,0 calibres, demostrado mediante cálculo o simulación antes del vuelo.",
      "Velocidad mínima de despegue de 55 km/h al abandonar la plataforma de lanzamiento, para que las aletas trabajen aerodinámicamente desde los primeros instantes del vuelo.",
    ],
    payloadRestrictions: [
      "La carga útil puede tener objetivos científicos, tecnológicos o de ingeniería.",
      "No se permite utilizar animales vertebrados como parte de la carga útil.",
    ],
    recoveryEvaluation: [
      { level: "verde", label: "Apertura óptima", description: "El paracaídas abre completamente y cumple correctamente su función." },
      { level: "amarillo", label: "Apertura parcial", description: "El paracaídas abre de manera incompleta o enredada, pero ayuda a disminuir la velocidad de descenso." },
      { level: "rojo", label: "Sin apertura", description: "El paracaídas no abre y no cumple su función." },
    ],
    stabilityEvaluation: [
      { level: "verde", label: "Muy estable", description: "El cohete asciende de manera recta y controlada." },
      { level: "amarillo", label: "Medianamente estable", description: "El cohete comienza a desviarse o torcerse durante el vuelo." },
      { level: "rojo", label: "Inestable", description: "El cohete se desvía considerablemente o pierde estabilidad durante el ascenso." },
    ],
    scoringCriteria: [
      "Precisión respecto a la altura objetivo (400 m o 650 m, según motor asignado).",
      "Cumplimiento de la relación empuje/peso mínima de 5:1.",
      "Margen de estabilidad estática mínimo de 2,0 calibres.",
      "Velocidad mínima de despegue de 55 km/h.",
      "Desempeño del sistema de recuperación.",
    ],
    mission: [
      "Diseña tu propia carga útil tipo CanSat de 250 a 300 g.",
      "Integra correctamente el motor asignado.",
      "Acércate lo máximo posible a los 400 m o 650 m de altura objetivo, según motor asignado.",
      "Cumple una relación mínima empuje/peso de 5:1.",
      "Demuestra un margen mínimo de estabilidad de 2,0 calibres.",
      "Alcanza al menos 55 km/h al abandonar la plataforma de lanzamiento.",
      "Registra la altura con un altímetro de cualquiera de estas marcas: Jolly Logic o Estes.",
      "Diseña un sistema de recuperación confiable.",
    ],
    documents: [],
  },
}

export function getContestCategoryContent(slug: string | null): CategoryContestContent | null {
  if (!slug) return null
  // CccdEventCategory.slug values are compound (e.g. "categoria-1-principiantes",
  // "categoria-2-intermedio") — this catalog is keyed by the bare
  // "categoria-N" prefix that determines the actual rules content, so match
  // on that prefix instead of requiring an exact string match.
  const prefixMatch = /^categoria-\d+/.exec(slug)
  const content = CONTEST_CATEGORIES[prefixMatch?.[0] ?? slug]
  if (!content && import.meta.env.DEV) {
    console.warn(`[CCCD] No contest content mapped for category slug "${slug}" — see cccdContestCategories.ts`)
  }
  return content ?? null
}
